import React, { useEffect, useMemo } from "react";
import { IonPage, IonToolbar, IonHeader, IonButtons, IonBackButton, IonContent, IonImg, IonButton } from "@ionic/react";
import { useParams } from "react-router-dom";
import DataController from "@controllers/DataController";
import { Capacitor } from "@capacitor/core";
import HeaderTitle from "@components/common/HeaderTitle";
import * as StringUtils from "@utils/stringUtils";
import ContentContainer from "@components/common/ContentContainer";
import { useTextToSpeech } from "@hooks/accessibility/useTextToSpeech";
import { Button_Audio_Play_White } from "@assets/images";
import { Button_Audio_Stop } from "@assets/images";
import { useRecoilValueLoadable } from "recoil";
import {
  AllProgressCheckStatuses,
  IAccessibilitySettings,
  ProgramModuleStatusHistory,
  ProgressCheckStatus,
} from "@interfaces/Interfaces";
import { accessibilityAtom, progressCheckDataAtom } from "@state/State";
import { useHidingTabBar } from "@hooks/tabs/useHidingTabBar";
import { format } from "date-fns";
import classNames from "classnames";

const AbsenceConversation: React.FC = () => {
  const params = useParams<{ programId: string }>();
  const accessibilitySettings = useRecoilValueLoadable<IAccessibilitySettings | null>(accessibilityAtom);

  useHidingTabBar();

  const allProgressCheckData = useRecoilValueLoadable<AllProgressCheckStatuses[] | null>(progressCheckDataAtom);
  const programProgressCheckData = allProgressCheckData.contents?.find(
    (item: AllProgressCheckStatuses) => item.programID === params.programId
  );
  const metadataProgressCheck = programProgressCheckData?.pCs.find(
    (item: ProgressCheckStatus) => item.pCId === "pMData"
  );

  const pauseData = metadataProgressCheck?.pMData?.data;
  const history = metadataProgressCheck?.pMData?.history;

  const pauseDataHistory = useMemo(
    () =>
      history
        .filter((item: ProgramModuleStatusHistory) => item.pMData?.AbsenceConversation || item.pMData?.ActionPlan)
        .sort(
          (a: ProgramModuleStatusHistory, b: ProgramModuleStatusHistory) =>
            new Date(b.date).getTime() - new Date(a.date).getTime()
        ),
    [history]
  );

  const absenceConversation = {
    AbsenceConversation: "",
    ActionPlan: "",
  };

  if (pauseData?.AbsenceConversation) {
    absenceConversation.AbsenceConversation = pauseData.AbsenceConversation;
  }

  if (pauseData?.ActionPlan) {
    absenceConversation.ActionPlan = pauseData.ActionPlan;
  }

  const { handleTTS, playing, stopTTS } = useTextToSpeech("absenceConversationContainer");

  useEffect(() => {
    return () => {
      stopTTS();
    };
  }, []);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar
          mode="ios"
          className="navBar"
          style={{
            maxWidth: DataController.isWeb() ? 980 : undefined,
            height: Capacitor.getPlatform() === "android" ? "54px" : "unset",
          }}
        >
          <IonButtons slot="start">
            <IonBackButton
              className="header-back-buttons"
              defaultHref={`/dashboard/program/${params.programId}`}
              text={DataController.getBackIconText()}
              icon={DataController.getBackIconType()}
              style={{
                marginLeft: Capacitor.getPlatform() === "android" ? 8 : 0,
                "--icon-font-size": Capacitor.getPlatform() === "android" ? "24px" : "30px",
              }}
            />
          </IonButtons>
          <HeaderTitle>{"Absence Conversation"}</HeaderTitle>
          <IonButtons slot="end">
            {accessibilitySettings.contents?.textToSpeech && (
              <IonButton
                className="header-button"
                mode="ios"
                onClick={() => handleTTS()}
              >
                <IonImg
                  src={playing ? Button_Audio_Stop : Button_Audio_Play_White}
                  className="headerIcon"
                />
              </IonButton>
            )}
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="page-background whiteBackground">
        <ContentContainer
          tabBarDisabled
          id="absenceConversationContainer"
          className="pt-0"
        >
          <div className="readEvidenceBlock">
            <div className="readEvidenceHeader">{"Absence Conversation"}</div>
            <div
              className="readEvidenceText"
              dangerouslySetInnerHTML={StringUtils.sanitizeHTML(absenceConversation.AbsenceConversation ?? "")}
            />
          </div>
          <div className="h-[1px] w-full bg-grey-30 mt-4" />
          <div className="readEvidenceBlock">
            <div className="readEvidenceHeader">{"Action Plan"}</div>
            <div
              className="readEvidenceText"
              dangerouslySetInnerHTML={StringUtils.sanitizeHTML(absenceConversation.ActionPlan ?? "")}
            />
          </div>
          {pauseDataHistory.length > 0 && (
            <>
              <div className="flex flex-row items-center justify-between mt-4 gap-4">
                <div className="h-[1px] flex-1 bg-grey-30" />
                <div className="text-acc-12px font-acc-600 leading-acc-lh text-grey-90">{"History"}</div>
                <div className="h-[1px] flex-1 bg-grey-30" />
              </div>
              {pauseDataHistory.map((item: ProgramModuleStatusHistory, index: number) => {
                const hasAbsenceConversation = item.pMData?.AbsenceConversation;
                const hasActionPlan = item.pMData?.ActionPlan;

                return (
                  <div
                    key={item.date}
                    className="mt-4"
                  >
                    <div className="readEvidenceText text-acc-15px !text-grey-90">
                      {"By "}
                      <b>{item.displayName}</b>
                      {" on "}
                      <b>{format(new Date(item.date), "d MMM yyyy")}</b>
                      {" at "}
                      <b>{format(new Date(item.date), "HH:mm")}</b>
                    </div>
                    <div className="mt-3">
                      {hasAbsenceConversation && (
                        <>
                          <div className="readEvidenceHeader">{"Absence Conversation"}</div>
                          <div
                            className="readEvidenceText"
                            dangerouslySetInnerHTML={StringUtils.sanitizeHTML(item.pMData?.AbsenceConversation ?? "")}
                          />
                        </>
                      )}
                      {hasActionPlan && (
                        <>
                          <div
                            className={classNames("readEvidenceHeader", {
                              "mt-4": hasAbsenceConversation,
                            })}
                          >
                            {"Action Plan"}
                          </div>
                          <div
                            className="readEvidenceText"
                            dangerouslySetInnerHTML={StringUtils.sanitizeHTML(item.pMData?.ActionPlan ?? "")}
                          />
                        </>
                      )}
                    </div>
                    {index !== pauseDataHistory.length - 1 && <div className="h-[1px] w-full bg-grey-30 mt-4" />}
                  </div>
                );
              })}
            </>
          )}
        </ContentContainer>
      </IonContent>
    </IonPage>
  );
};

export default AbsenceConversation;
